import { Component, computed, signal, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { DeviceTrialService } from '@services/device-trial/device-trial.service';
import { SessionStorageService } from '@services/session-storage/session-storage.service';
import dayjs from 'dayjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

const SEVEN_DAYS_IN_SECONDS = 7 * 86400;
const SESSION_KEY_PREFIX = 'trial_banner_dismissed_';

@Component({
    selector: 'app-device-trial-banner',
    imports: [RouterLink, TranslateModule],
    templateUrl: './device-trial-banner.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './device-trial-banner.component.scss'
})
export class DeviceTrialBannerComponent {
    private dismissedIds = signal<Set<string>>(new Set());

    visibleTrials = computed(() => {
        const now = dayjs().unix();
        const dismissed = this.dismissedIds();

        return this.deviceTrialService.trials().filter(trial => {
            const isExpiringSoon = trial.expiresAt - now <= SEVEN_DAYS_IN_SECONDS && trial.expiresAt > now;
            const isDismissed = dismissed.has(trial.locationId) || !!this.sessionStorageService.getItem(`${SESSION_KEY_PREFIX}${trial.locationId}`);
            return isExpiringSoon && !isDismissed;
        });
    });

    bannerMessage = computed(() => {
        const trials = this.visibleTrials();
        if (trials.length === 0) return null;
        if (trials.length === 1) {
            return this.translate.instant('navbar.trial_expiring_soon', { locationName: trials[0].locationName });
        }
        return this.translate.instant('navbar.trial_expiring_soon_multiple', { count: trials.length });
    });

    constructor(
        private deviceTrialService: DeviceTrialService,
        private sessionStorageService: SessionStorageService,
        private translate: TranslateService
    ) {}

    dismiss() {
        const currentDismissed = new Set(this.dismissedIds());
        for (const trial of this.visibleTrials()) {
            currentDismissed.add(trial.locationId);
            this.sessionStorageService.setItem(`${SESSION_KEY_PREFIX}${trial.locationId}`, 'true');
        }
        this.dismissedIds.set(currentDismissed);
    }
}
